import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useSeo } from "@/lib/seo";
import { SEARCH_GROUPS } from "@/i18n/translations";
import SearchBar from "@/components/SearchBar";

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const { lang, L, t } = useLang();
  useSeo({ title: lang === "fr" ? "Recherche" : "Search", lang, robots: "noindex,follow" });
  const [data, setData] = useState({ total: 0, groups: {} });
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!q) { setData({ total: 0, groups: {} }); setLoading(false); return; }
    setLoading(true);
    api.get("/global-search", { params: { q } }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [q]);

  useEffect(() => { api.get("/suggestions").then(({ data }) => setSuggestions(data)).catch(() => {}); }, []);

  const itemTitle = (key, it) => {
    if (key === "markets") return L(it, "country_name") || it.country;
    if (key === "partners") return it.organization;
    if (key === "experts") return it.name;
    return L(it, "title") || L(it, "product") || it.title_fr || "—";
  };

  return (
    <div>
      <section className="border-b border-tertiary bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
          <SearchBar suggestions={suggestions} large />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
        <p className="mb-8 text-sm text-anthracite/60" data-testid="search-summary">
          {t("results_for")} <span className="font-medium text-anthracite">“{q}”</span> — {data.total}
        </p>

        {loading ? (
          <p className="py-16 text-center text-anthracite/40">…</p>
        ) : data.total === 0 ? (
          <div className="border border-dashed border-tertiary bg-white py-20 text-center" data-testid="search-empty">
            <p className="text-anthracite/50">{t("no_results")}</p>
          </div>
        ) : (
          <div className="space-y-12">
            {SEARCH_GROUPS.map((g) => {
              const list = data.groups[g.key] || [];
              if (list.length === 0) return null;
              return (
                <div key={g.key} data-testid={`search-group-${g.key}`}>
                  <div className="mb-4 flex items-center justify-between border-b border-tertiary pb-2">
                    <h2 className="font-serif text-2xl text-anthracite">{g[lang]}</h2>
                    <span className="text-xs text-anthracite/40">{list.length}</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {list.map((it) => (
                      <Link key={it.id} to={`${g.path}/${it.slug}`} data-testid={`search-result-${it.slug}`} className="group flex items-start justify-between gap-3 border border-tertiary bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-champagne">
                        <div>
                          <p className="font-medium text-anthracite">{itemTitle(g.key, it)}</p>
                          {(L(it, "summary") || it.region) && <p className="mt-1 text-xs text-anthracite/50 line-clamp-2">{L(it, "summary") || it.region}</p>}
                        </div>
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-anthracite/30 transition-transform group-hover:translate-x-0.5 group-hover:text-champagne" />
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
